import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/supabase';
import { useAuth, canSeeCosts } from '@/context/AuthContext';
import { fmtUSD, fmtPct } from '@/lib/calc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, Camera, GitBranch, Check, Send, Zap, Edit3 } from 'lucide-react';
import { toast } from 'sonner';

const TABS = ['Overview', 'Recipe', 'Versions'];

export default function ProductDetail({ sku, onClose, onChanged }) {
  const { role } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('Overview');
  const [busy, setBusy] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [priceEdit, setPriceEdit] = useState('');
  const [minsEdit, setMinsEdit] = useState('');

  const showCosts = canSeeCosts(role) || role === 'purchasing';
  const showPrice = ['admin','manager','sales','purchasing'].includes(role);
  const canManage = ['admin','manager'].includes(role);

  useEffect(() => {
    if (!sku) { setData(null); return; }
    setEditMode(false);
    (async () => {
      try { setData(await apiFetch(`/catalog/products/${sku}`)); }
      catch (e) { toast.error(e.message); onClose(); }
    })();
  }, [sku]);

  if (!sku) return null;
  if (!data) return null;
  const { product, current_version: cv, ingredients, versions, rollup } = data;

  const call = async (path, opts) => {
    setBusy(true);
    try {
      await apiFetch(path, opts);
      const fresh = await apiFetch(`/catalog/products/${sku}`);
      setData(fresh);
      onChanged?.();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('photo', file);
    setBusy(true);
    try {
      await apiFetch(`/catalog/products/${sku}/image`, { method: 'POST', body: fd });
      toast.success('Image updated');
      const fresh = await apiFetch(`/catalog/products/${sku}`);
      setData(fresh); onChanged?.();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const workflowActions = () => {
    if (!cv || !canManage) return null;
    const s = cv.workflow_state;
    const btns = [];
    if (['Approved','Active','Archived'].includes(s)) {
      btns.push({ label: 'Fork to draft', icon: GitBranch,
        onClick: () => call(`/catalog/recipe-versions/${cv.version_code}/draft`, { method: 'POST' })
          .then(() => toast.success('Draft created — switch to Versions tab')),
        test: 'action-fork-draft' });
    }
    if (s === 'Draft') {
      btns.push({ label: 'Submit for review', icon: Send,
        onClick: () => call(`/catalog/recipe-versions/${cv.version_code}/submit-review`, { method: 'POST', body: JSON.stringify({}) })
          .then(() => toast.success('Sent to review')),
        test: 'action-submit-review' });
    }
    if (s === 'Review' || s === 'Draft') {
      btns.push({ label: 'Approve', icon: Check,
        onClick: () => call(`/catalog/recipe-versions/${cv.version_code}/approve`, { method: 'POST', body: JSON.stringify({}) })
          .then(() => toast.success('Approved')),
        test: 'action-approve' });
    }
    if (s === 'Approved') {
      btns.push({ label: 'Activate', icon: Zap,
        onClick: () => call(`/catalog/recipe-versions/${cv.version_code}/activate`, { method: 'POST', body: JSON.stringify({}) })
          .then(() => toast.success('Version activated')),
        test: 'action-activate' });
    }
    return (
      <div className="flex flex-wrap gap-2 mt-4">
        {btns.map(b => (
          <Button key={b.label} size="sm" variant="outline" disabled={busy}
            onClick={b.onClick} className="rounded-sm" data-testid={b.test}>
            <b.icon className="h-3.5 w-3.5 mr-1" /> {b.label}
          </Button>
        ))}
      </div>
    );
  };

  const saveEdits = async () => {
    const patch = {};
    if (priceEdit !== '') patch.selling_price = Number(priceEdit);
    if (minsEdit !== '') patch.designer_minutes = Number(minsEdit);
    if (!Object.keys(patch).length) { setEditMode(false); return; }
    await call(`/catalog/recipe-versions/${cv.version_code}`, {
      method: 'PATCH', body: JSON.stringify(patch)
    });
    setEditMode(false);
    setPriceEdit(''); setMinsEdit('');
    toast.success('Draft updated');
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40" onClick={onClose} data-testid="product-detail-drawer">
      <aside className="absolute right-0 top-0 h-full w-full sm:max-w-3xl bg-card border-l border-border overflow-y-auto"
             onClick={e => e.stopPropagation()}>
        <header className="sticky top-0 z-10 bg-card border-b border-border p-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="velvet-eyebrow flex items-center gap-2 flex-wrap">
              <span className="font-mono">{product.product_sku}</span>
              {product.is_draft && <span className="text-[9px] px-1.5 py-0.5 border border-border text-muted-foreground uppercase tracking-wider">DRAFT</span>}
              {product.is_demo && <span className="text-[9px] px-1.5 py-0.5 border border-border text-muted-foreground uppercase tracking-wider">DEMO</span>}
              {cv?.workflow_state && <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border border-border">{cv.workflow_state}</span>}
            </div>
            <h2 className="font-serif text-3xl tracking-tight mt-1" data-testid="product-name">{product.name}</h2>
            <div className="text-xs text-muted-foreground mt-1">{product.collection_name || product.category} · {product.occasion || '—'}</div>
          </div>
          <button onClick={onClose} className="p-1 shrink-0" data-testid="close-detail"><X className="h-5 w-5" /></button>
        </header>

        <div className="border-b border-border sticky top-[100px] bg-card z-10 flex">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-5 py-3 text-sm border-b-2 transition-colors ${tab===t ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid={`tab-${t.toLowerCase()}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-6">
          {tab === 'Overview' && (
            <div className="space-y-6">
              <div className="aspect-[16/9] bg-[hsl(var(--muted))] border border-border overflow-hidden">
                {product.photo_url ? (
                  <img src={product.photo_url} alt={product.name} className="w-full h-full object-cover" />
                ) : <div className="w-full h-full flex items-center justify-center text-muted-foreground">No image</div>}
              </div>
              {canManage && (
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer border border-border px-3 py-2 bg-background hover:bg-[hsl(var(--muted))] rounded-sm" data-testid="upload-image-btn">
                  <Camera className="h-4 w-4" /> Upload / replace image
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => uploadImage(e.target.files?.[0])} />
                </label>
              )}
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <Info label="Collection" value={product.collection_name || product.category || '—'} />
                <Info label="Occasion / Season" value={product.occasion || '—'} />
                <Info label="Catalog base" value={product.catalog_base || '—'} />
                <Info label="Brand" value={product.brand || '—'} />
                <Info label="Recipe ID" value={product.recipe_id || '—'} mono />
                <Info label="Current version" value={product.current_recipe_version_id || '—'} mono />
                {showPrice && (
                  <Info label="Selling price"
                    value={product.current_price != null
                      ? <span className="font-mono">{fmtUSD(product.current_price)}</span>
                      : <span className="text-muted-foreground">Price pending</span>} />
                )}
                <Info label="Active for sale" value={product.active_for_sale ? 'Yes' : 'No'} />
              </dl>
              {showCosts && rollup && (
                <div className="border border-border bg-[hsl(var(--soft-blush))]/40 p-5" data-testid="cost-rollup">
                  <div className="velvet-eyebrow mb-3">Cost roll-up (current recipe)</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <Metric label="Flowers" value={fmtUSD(rollup.flowers_cost)} />
                    <Metric label="Materials" value={fmtUSD(rollup.materials_cost)} />
                    <Metric label="Labor" value={fmtUSD(rollup.labor_cost)} sub={`${cv?.designer_minutes || 0} min @ ${fmtUSD(rollup.labor_rate)}/hr`} />
                    <Metric label="Total cost" value={fmtUSD(rollup.total_cost)} accent />
                    <Metric label="Selling price" value={fmtUSD(rollup.selling_price)} />
                    <Metric label="Gross profit" value={fmtUSD(rollup.gross_profit)} accent />
                    <Metric label="Gross margin" value={fmtPct(rollup.gross_margin)} accent />
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'Recipe' && (
            <div className="space-y-5">
              {cv ? (
                <>
                  <div className="flex items-baseline justify-between">
                    <div>
                      <div className="velvet-eyebrow">{cv.version_code}</div>
                      <div className="font-serif text-xl">{cv.season} · {cv.size || '—'}</div>
                    </div>
                    {canManage && cv.workflow_state === 'Draft' && (
                      <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEditMode(v => !v)} data-testid="edit-draft-btn">
                        <Edit3 className="h-3.5 w-3.5 mr-1" /> {editMode ? 'Cancel' : 'Edit draft'}
                      </Button>
                    )}
                  </div>
                  {editMode && (
                    <div className="grid grid-cols-2 gap-3 p-3 border border-border bg-background/60 rounded-sm">
                      <div>
                        <Label>Selling price</Label>
                        <Input type="number" step="0.01" defaultValue={cv.selling_price ?? ''} onChange={e => setPriceEdit(e.target.value)} className="rounded-sm" data-testid="edit-price-input" />
                      </div>
                      <div>
                        <Label>Production minutes</Label>
                        <Input type="number" defaultValue={cv.designer_minutes ?? ''} onChange={e => setMinsEdit(e.target.value)} className="rounded-sm" data-testid="edit-mins-input" />
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <Button size="sm" onClick={saveEdits} disabled={busy} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="save-draft-edits">Save</Button>
                      </div>
                    </div>
                  )}
                  <div className="border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-background/60">
                        <tr className="border-b border-border text-left">
                          <th className="px-3 py-2 velvet-eyebrow">SKU</th>
                          <th className="px-3 py-2 velvet-eyebrow">Ingredient</th>
                          <th className="px-3 py-2 velvet-eyebrow text-right w-14">Qty</th>
                          <th className="px-3 py-2 velvet-eyebrow w-24">Unit</th>
                          <th className="px-3 py-2 velvet-eyebrow w-24">Verify</th>
                          {showCosts && <th className="px-3 py-2 velvet-eyebrow text-right w-24">$/unit</th>}
                          {showCosts && <th className="px-3 py-2 velvet-eyebrow text-right w-24">Line</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {ingredients.map(i => (
                          <tr key={i.id} className="border-b border-border last:border-b-0" data-testid={`ingredient-row-${i.ingredient_sku}`}>
                            <td className="px-3 py-2 font-mono text-xs">{i.ingredient_sku}</td>
                            <td className="px-3 py-2">{i.ingredient_name}<span className="text-muted-foreground"> · {i.color}</span></td>
                            <td className="px-3 py-2 text-right velvet-money">{i.qty}</td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">{i.recipe_unit || '—'}</td>
                            <td className="px-3 py-2 text-xs">
                              <span className={`px-1.5 py-0.5 border text-[10px] uppercase tracking-wider ${i.verification === 'READY' ? 'border-[hsl(var(--muted-green))]/50 text-[hsl(var(--muted-green-dark))]' : 'border-[hsl(var(--soft-blush-dark))] text-foreground'}`}>{i.verification}</span>
                            </td>
                            {showCosts && <td className="px-3 py-2 text-right velvet-money">{fmtUSD(i.chicago_cost_unit)}</td>}
                            {showCosts && <td className="px-3 py-2 text-right velvet-money">{fmtUSD(i.line_cost)}</td>}
                          </tr>
                        ))}
                        {ingredients.length === 0 && <tr><td colSpan={showCosts?7:5} className="p-6 text-center text-muted-foreground">No ingredients yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <Info label="Design instructions" value={cv.approval_source_notes || cv.control_note || '—'} />
                    <Info label="Substitutions / notes" value={cv.migration_note || '—'} />
                    <Info label="Production minutes" value={`${cv.designer_minutes ?? '—'} min`} />
                    <Info label="Packaging" value={showCosts ? fmtUSD(cv.packaging_cost) : (cv.packaging_cost != null ? 'Included' : '—')} />
                  </div>
                  {workflowActions()}
                </>
              ) : <p className="text-sm text-muted-foreground">No current recipe version.</p>}
            </div>
          )}

          {tab === 'Versions' && (
            <div className="space-y-3" data-testid="versions-tab">
              {versions.length === 0 && <p className="text-sm text-muted-foreground">No versions.</p>}
              {versions.map(v => (
                <div key={v.version_code} className="border border-border p-4" data-testid={`version-${v.version_code}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-mono text-xs">{v.version_code}</div>
                      <div className="font-serif text-lg">{v.season} · v{v.version}</div>
                      <div className="text-xs text-muted-foreground mt-1">{v.effective_from ? new Date(v.effective_from).toLocaleDateString() : '—'} → {v.effective_to ? new Date(v.effective_to).toLocaleDateString() : '—'}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border border-border">{v.workflow_state}</span>
                  </div>
                  {showPrice && v.selling_price != null && (
                    <div className="mt-2 text-sm velvet-money">{fmtUSD(v.selling_price)} · {v.designer_minutes || 0} min</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="velvet-eyebrow mb-1">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}
function Metric({ label, value, sub, accent }) {
  return (
    <div className={`border border-border p-3 ${accent ? 'bg-card' : 'bg-background/60'}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className="velvet-money text-base mt-1">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
